<script setup lang="ts">
const props = defineProps({
  direction: {
    default: 'vertical',
    type: String as PropType<'horizontal' | 'vertical'>,
  },
  interval: {
    default: 4000,
    type: Number,
  },
  once: {
    default: false,
    type: Boolean,
  },
  slots: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
})

const emit = defineEmits<{
  done: []
  reset: []
}>()

// Current slot being displayed
const currentSlotIndex = defineModel<number>({ default: 0 })
const intervalRef = ref<ReturnType<typeof useRafInterval>>()
const hasReachedLastItem = ref(false)

// Function to reset the animation
// Only works when once is true
function reset(): void {
  if (props.once) {
    currentSlotIndex.value = 0
    hasReachedLastItem.value = false
    startRotation()
    emit('reset')
  }
}

// Function to change the current slot
function changeSlot(): void {
  const nextIndex = currentSlotIndex.value + 1

  // Check if we're about to reach the last item
  if (nextIndex >= props.slots.length - 1) {
    hasReachedLastItem.value = true

    // If once is true and we're at the second-to-last item, move to the last item and stop
    if (props.once && intervalRef.value) {
      currentSlotIndex.value = props.slots.length - 1
      intervalRef.value.pause()
      intervalRef.value = undefined

      // Emit the 'done' event when we reach the last item with once=true
      emit('done')

      return
    }
  }

  // Normal rotation (with wrapping)
  currentSlotIndex.value = nextIndex % props.slots.length
}

// Handle visibility change to pause/resume rotation when tab is inactive
function handleVisibilityChange(): void {
  if (document.visibilityState !== 'visible' && intervalRef.value) {
    intervalRef.value.pause()
    intervalRef.value = undefined
  }
  else if (document.visibilityState === 'visible') {
    startRotation()
  }
}

// Start the rotation interval
function startRotation(): void {
  // Only start if we have multiple slots
  if (props.slots.length <= 1) {
    return
  }

  // Don't start if interval is set to 0 (manual mode)
  if (props.interval === 0) {
    // Clear any existing interval if it exists
    if (intervalRef.value) {
      intervalRef.value.pause()
      intervalRef.value = undefined
    }

    return
  }

  // Don't restart if we've reached the last item and once is true
  if (props.once && hasReachedLastItem.value && currentSlotIndex.value === props.slots.length - 1) {
    return
  }

  // Clear any existing interval first
  if (intervalRef.value) {
    intervalRef.value.pause()
  }

  // Set new interval
  intervalRef.value = useRafInterval(changeSlot, props.interval)
}

onMounted(() => {
  startRotation()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  if (intervalRef.value) {
    intervalRef.value.pause()
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

const transitionEnterFromClass = computed(() => {
  return props.direction === 'horizontal' ? 'opacity-0 translate-x-4' : 'opacity-0 translate-y-4'
})

const transitionLeaveToClass = computed(() => {
  return props.direction === 'horizontal' ? 'opacity-0 -translate-x-4' : 'opacity-0 -translate-y-4'
})

// Restart rotation when slots change
watch(
  () => props.slots,
  () => {
    // Reset index if it's out of bounds
    if (currentSlotIndex.value >= props.slots.length) {
      currentSlotIndex.value = 0
    }
    // Reset the reached last item flag when slots change
    hasReachedLastItem.value = false
    startRotation()
  },
  { deep: true },
)

// Watch for changes to the once prop
watch(
  () => props.once,
  (newValue) => {
    if (newValue && hasReachedLastItem.value && currentSlotIndex.value === props.slots.length - 1 && intervalRef.value) {
      intervalRef.value.pause()
      intervalRef.value = undefined
    }
    else if (!newValue && !intervalRef.value) {
      startRotation()
    }
  },
)

// Watch for changes to the interval prop
watch(
  () => props.interval,
  (newValue) => {
    if (newValue === 0 && intervalRef.value) {
      // Clear interval when interval is set to 0
      intervalRef.value.pause()
      intervalRef.value = undefined
    }
    else if (newValue > 0 && !intervalRef.value) {
      // Restart rotation if interval becomes non-zero
      startRotation()
    }
  },
)

defineExpose({ reset })
</script>

<template>
  <div class="animation-rotating">
    <Transition
      enter-active-class="transition-base"
      :enter-from-class="transitionEnterFromClass"
      enter-to-class="opacity-100 translate-x-0 translate-y-0"
      leave-active-class="transition-base"
      leave-from-class="opacity-100 translate-x-0 translate-y-0"
      :leave-to-class="transitionLeaveToClass"
      mode="out-in"
    >
      <div :key="slots[currentSlotIndex]">
        <slot :name="slots[currentSlotIndex]" />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.transition-base {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>
